"use client"

import {
  invalidatePendingTurnstileLoad,
  loadTurnstile,
  withTimeout,
  type TurnstileApi,
  type TurnstileWidgetId,
} from "./turnstile-client"

const TURNSTILE_READY_TIMEOUT_MILLIS = 2500

export type LoginTurnstileFailure =
  | "failure"
  | "expired"
  | "timeout"
  | "unsupported"
  | "unavailable"

export class LoginTurnstileError extends Error {
  constructor(readonly reason: LoginTurnstileFailure) {
    super(`turnstile_${reason}`)
    this.name = "LoginTurnstileError"
  }
}

/**
 * Waits for the security check to load and render. Any failure here (blocked or slow script,
 * missing container, timeout) happens in this browser before a request is sent, so it is a
 * security-check failure and never a statement about the account.
 */
export async function readyLoginTurnstile<T>(widget: Promise<T>, onTimeout: () => void): Promise<T> {
  try {
    return await withTimeout(widget, TURNSTILE_READY_TIMEOUT_MILLIS, onTimeout)
  } catch {
    throw new LoginTurnstileError("unavailable")
  }
}

type PendingTokenRequest = {
  resolve: (token: string) => void
  reject: (error: LoginTurnstileError) => void
}

export type LoginTurnstileWidgetOptions = {
  siteKey: string
  locale: string
  getContainer: () => HTMLElement | null
  isAttemptActive: () => boolean
  focusChallenge: () => void
  setChallengeVisible: (visible: boolean) => void
  loadApi?: () => Promise<TurnstileApi>
  cancelApiLoad?: () => void
}

/**
 * One widget session per mounted login screen. A remount, including React StrictMode's
 * simulated one, disposes this session and starts a new one, so a page-load token is
 * always prepared while the user types.
 */
export class LoginTurnstileWidget {
  private api: TurnstileApi | null = null
  private widgetId: TurnstileWidgetId | null = null
  private initialization: Promise<TurnstileWidgetId> | null = null
  private preparedToken: string | null = null
  private pendingTokenRequest: PendingTokenRequest | null = null
  private preflightFailure: LoginTurnstileFailure | null = null
  private generation = 0
  private disposed = false
  private challengeVisible = false

  constructor(private readonly options: LoginTurnstileWidgetOptions) {}

  start(): void {
    void this.ensureWidget().catch(() => undefined)
  }

  dispose(): void {
    this.disposed = true
    this.invalidateInitialization()
    this.removeWidget()
  }

  async getToken(): Promise<string> {
    await readyLoginTurnstile(this.ensureWidget(), () => this.cancelPendingSetup())
    if (!this.options.isAttemptActive()) {
      throw new Error("turnstile_attempt_inactive")
    }

    const preparedToken = this.preparedToken
    if (preparedToken) {
      this.preparedToken = null
      return preparedToken
    }

    const preflightFailure = this.preflightFailure
    if (preflightFailure) {
      this.preflightFailure = null
      throw new LoginTurnstileError(preflightFailure)
    }

    const tokenPromise = new Promise<string>((resolve, reject) => {
      this.pendingTokenRequest = { resolve, reject }
    })
    if (this.challengeVisible) this.options.focusChallenge()
    return tokenPromise
  }

  reset(): void {
    this.rejectPendingTokenRequest("failure")
    this.setChallengeVisible(false)
    this.preparedToken = null
    this.preflightFailure = null
    const { api, widgetId } = this
    if (!api || !widgetId) return
    try {
      api.reset(widgetId)
    } catch {
      this.removeWidget()
      this.generation += 1
    }
  }

  private async ensureWidget(): Promise<TurnstileWidgetId> {
    if (!this.options.siteKey) {
      throw new Error("turnstile_not_configured")
    }
    if (this.widgetId) {
      return this.widgetId
    }
    if (this.initialization) {
      return this.initialization
    }

    const initialization = this.renderWidget(++this.generation)
    this.initialization = initialization
    try {
      return await initialization
    } finally {
      if (this.initialization === initialization) {
        this.initialization = null
      }
    }
  }

  private async renderWidget(generation: number): Promise<TurnstileWidgetId> {
    const api = await (this.options.loadApi ?? loadTurnstile)()
    if (this.disposed || generation !== this.generation) {
      throw new Error("turnstile_component_inactive")
    }
    const container = this.options.getContainer()
    if (!container) {
      throw new Error("turnstile_container_missing")
    }

    container.replaceChildren()
    const widgetId = api.render(container, {
      sitekey: this.options.siteKey,
      action: "integration_hub_login",
      execution: "render",
      appearance: "interaction-only",
      theme: "light",
      size: "flexible",
      language: this.options.locale,
      "response-field": false,
      retry: "auto",
      "refresh-expired": "auto",
      "refresh-timeout": "auto",
      callback: (token) => this.acceptToken(token),
      "error-callback": () => this.handleFailure("failure"),
      "expired-callback": () => this.handleFailure("expired"),
      "timeout-callback": () => this.handleFailure("timeout"),
      "unsupported-callback": () => this.handleFailure("unsupported"),
      "before-interactive-callback": () => {
        this.setChallengeVisible(true)
        if (this.options.isAttemptActive()) this.options.focusChallenge()
      },
      "after-interactive-callback": () => this.setChallengeVisible(false),
    })
    this.api = api
    this.widgetId = widgetId
    return widgetId
  }

  private acceptToken(token: string): void {
    this.setChallengeVisible(false)
    this.preflightFailure = null
    const pendingRequest = this.pendingTokenRequest
    if (pendingRequest) {
      this.pendingTokenRequest = null
      pendingRequest.resolve(token)
    } else {
      this.preparedToken = token
    }
  }

  private handleFailure(reason: LoginTurnstileFailure): void {
    this.preparedToken = null
    if (this.pendingTokenRequest) {
      this.rejectPendingTokenRequest(reason)
      return
    }
    this.preflightFailure = reason
  }

  private rejectPendingTokenRequest(reason: LoginTurnstileFailure): void {
    const pendingRequest = this.pendingTokenRequest
    this.pendingTokenRequest = null
    pendingRequest?.reject(new LoginTurnstileError(reason))
  }

  private removeWidget(): void {
    this.setChallengeVisible(false)
    this.preparedToken = null
    this.preflightFailure = null
    this.rejectPendingTokenRequest("failure")
    const widgetId = this.widgetId
    if (!widgetId) return
    try {
      this.api?.remove(widgetId)
    } catch {
      // The provider may already have removed a failed widget.
    }
    this.widgetId = null
  }

  private invalidateInitialization(): void {
    this.generation += 1
    this.initialization = null
  }

  private cancelPendingSetup(): void {
    this.invalidateInitialization()
    ;(this.options.cancelApiLoad ?? invalidatePendingTurnstileLoad)()
  }

  private setChallengeVisible(visible: boolean): void {
    this.challengeVisible = visible
    this.options.setChallengeVisible(visible)
  }
}
